import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
  Pressable,
  Platform,
  StatusBar,
  Alert,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import { useNavigation, useRoute } from '@react-navigation/native';
import { callApi, formatDate, getUserId } from '../Helper/Helper';
import mystyles from '../../assets/styles/mystyles';

const KIND = {
  EAT: 'Eat',
  LIVE: 'Live',
  MOVE: 'Move',
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const ENDPOINT_URL = 'https://api.eabu.net';

function endpointFor(kind) {
  switch (kind) {
    case KIND.EAT:
      return 'eabu-eat-consumer';
    case KIND.LIVE:
      return 'eabu-live-consumer';
    case KIND.MOVE:
      return 'eabu-move-consumer';
    default:
      return 'eabu-eat-consumer';
  }
}

// Helper to determine the offer endpoint based on the request category
function offerEndpointFor(kind) {
  switch (kind) {
    case KIND.EAT:
      return 'offer-eat';
    case KIND.LIVE:
      return 'offer-live';
    case KIND.MOVE:
      return 'offer-move';
    default:
      return 'offer-eat';
  }
}

// Sub-component to fetch and display count for each card
const OfferCountPill = ({ kind, userId, itemId, onPress }) => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const endpoint = offerEndpointFor(kind);
        const url = `${endpoint}/${encodeURIComponent(userId)}/${encodeURIComponent(itemId)}`;
        const res = await callApi('GET', url);
        // Assuming response structure has Items or Count
        const items = res?.Items || (Array.isArray(res) ? res : []);
        setCount(items.length);
      } catch (e) {
        console.warn('Failed to fetch count for', itemId, e);
        setCount(0);
      }
    };
    fetchCount();
    // Set up polling interval every 5 minutes (300,000 ms)
    const intervalId = setInterval(() => {
      fetchCount();
    }, 300000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [kind, userId, itemId]);




  return (
    <Pressable
      style={({ pressed }) => [
        styles.offersPill,
        { opacity: pressed ? 0.7 : 1 }
      ]}
      onPress={onPress}
      hitSlop={10}
    >
      <Text style={styles.offersPillText}>
        Offers {count !== null ? `(${count})` : ''}
      </Text>
    </Pressable>
  );
};

const AllMyAsks = ({ endpoint, userId, kind }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const navigation = useNavigation();

  useEffect(() => {
    const fetchAsks = async () => {
      if (!userId) return;
      setLoading(true);
      setError('');
      setData(null);

      //add 11111 as zip code , it wont be used
      try {
        const json = await callApi('GET', `${endpoint}/11111/${encodeURIComponent(userId)}`);
        setData(json);
      } catch (e) {
        setError(e?.message || 'Something went wrong while fetching your asks.');
      } finally {
        setLoading(false);
      }
    };

    fetchAsks();
  }, [endpoint, userId]);

  const handleViewOffers = (item) => {
    // Check various common ID fields
    const itemId = item?.ID ?? item?.id ?? item?.itemId ?? item?.pk;

    if (!itemId) {
      Alert.alert('Error', 'Could not identify this request ID.');
      return;
    }

    navigation.navigate('ItemOffers', {
      endpoint: offerEndpointFor(kind),
      userId,
      itemId,
    });
  };

  const handleRepost = async (item) => {
    const itemId = item?.ID ?? item?.id ?? item?.itemId ?? item?.pk;
    if (!itemId) {
      Alert.alert('Error', 'Could not identify this request ID.');
      return;
    }

    try {
      const itemZipcode = item?.zipcode ?? item?.zip ?? item?.ZIP ?? item?.Zipcode ?? item?.Zip;
      const payload = {
        itemId: itemId,
        serviceCategory: kind,
        zipcode: itemZipcode,
      };


      await callApi('POST', '/consumer-repost', {body: payload});
      Alert.alert('Reposted', 'Your request is live again.');
    } catch (e) {
      Alert.alert('Error', e?.message || 'Failed to repost. Please try again.');
    }
  };

  if (loading) {
    return (
      <View style={mystyles.centered}>
        <ActivityIndicator size="large" color="#0A84FF" />
        <Text style={mystyles.loadingText}>Loading asks...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={mystyles.errorBox}>
        <Text style={mystyles.errorText}>{error}</Text>
      </View>
    );
  }

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (typeof data === 'object' && data !== null) {
    const arrayKey = Object.keys(data).find((k) => Array.isArray(data[k]));
    if (arrayKey) {
      items = data[arrayKey];
    }
  }

  if (items.length === 0) {
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <Text style={{ color: '#666' }}>No asks found.</Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
      {items.map((item, idx) => {
        const rowKey = item?.id ?? item?.ID ?? item?.pk ?? String(idx);
        const itemId = item?.ID ?? item?.id ?? item?.itemId ?? item?.pk;
        // Fallback fields based on likely schema
        const title = item?.title ?? item?.itemDescription ?? 'Request';
        const description = item?.description ?? item?.needDescription ?? item?.details;
        const date = item?.createdAt || item?.CreatedAt;
        const dateMs = date ? new Date(date).getTime() : null;
        const isExpired = Number.isFinite(dateMs) && Date.now() - dateMs > THIRTY_DAYS_MS;

        return (
          <View
            key={rowKey}
            style={[mystyles.card, idx % 2 === 1 && mystyles.cardAlt]}
          >
            <Text style={mystyles.title}>{title}</Text>

            {!!description && (
              <Text style={mystyles.description}>{description}</Text>
            )}

            {isExpired && (
              <View style={styles.expiredRow}>
                <Text style={styles.expiredText}>Expired</Text>
                <Pressable onPress={() => handleRepost(item)} hitSlop={10}>
                  <Text style={styles.repostLink}>Repost</Text>
                </Pressable>
              </View>
            )}

            <View style={styles.footerRow}>
              {!!date && (
                <Text style={styles.postedDateText}>
                  Posted: {formatDate(date)}
                </Text>
              )}

              <OfferCountPill
                kind={kind}
                userId={userId}
                itemId={itemId}
                onPress={() => handleViewOffers(item)}
              />
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
};

export default function MyAsks() {
  const route = useRoute();
  const routeUserId = route?.params?.userId;
  const [userId, setUserId] = useState(routeUserId || '');
  const [activeTab, setActiveTab] = useState(KIND.EAT);

  useEffect(() => {
    (async () => {
      if (!userId) {
        try {
          const uid = await getUserId();
          setUserId(uid || '');
        } catch {
          // ignore
        }
      }
    })();
  }, [userId]);

  return (
    <SafeAreaView style={mystyles.safe}>
      <StatusBar
        barStyle={Platform.OS === 'ios' ? 'light-content' : 'light-content'}
        backgroundColor="#0A84FF"
      />

      <View style={styles.headerContainer}>
        <Text style={styles.headerTitle}>My Asks</Text>
      </View>

      <View style={styles.tabContainer}>
        <Pressable
          onPress={() => setActiveTab(KIND.EAT)}
          style={[styles.tabButton, activeTab === KIND.EAT && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === KIND.EAT && styles.tabTextActive]}>
            Food
          </Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab(KIND.LIVE)}
          style={[styles.tabButton, activeTab === KIND.LIVE && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === KIND.LIVE && styles.tabTextActive]}>
            Household
          </Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab(KIND.MOVE)}
          style={[styles.tabButton, activeTab === KIND.MOVE && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === KIND.MOVE && styles.tabTextActive]}>
            Moving
          </Text>
        </Pressable>
      </View>

      <View style={mystyles.content}>
        {userId ? (
          <>
            {activeTab === KIND.EAT && (
              <AllMyAsks endpoint={endpointFor(KIND.EAT)} userId={userId} kind={KIND.EAT} />
            )}
            {activeTab === KIND.LIVE && (
              <AllMyAsks endpoint={endpointFor(KIND.LIVE)} userId={userId} kind={KIND.LIVE} />
            )}
            {activeTab === KIND.MOVE && (
              <AllMyAsks endpoint={endpointFor(KIND.MOVE)} userId={userId} kind={KIND.MOVE} />
            )}
          </>
        ) : (
          <View style={mystyles.centered}>
            <ActivityIndicator size="large" color="#0A84FF" />
            <Text style={mystyles.loadingText}>Loading user...</Text>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    padding: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  tabContainer: {
    flexDirection: 'row',
    marginHorizontal: 10,
    marginTop: 10,
    marginBottom: 10,
    backgroundColor: '#f1f3f5',
    borderRadius: 30,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 25,
    justifyContent: 'center',
  },
  tabButtonActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontWeight: '600',
    color: '#868e96',
    fontSize: 12,
  },
  tabTextActive: {
    color: '#343a40',
    fontWeight: '700',
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  postedDateText: {
    fontSize: 12,
    color: '#868e96',
    fontWeight: '500',
  },
  expiredRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  expiredText: {
    color: '#D14343',
    fontWeight: '700',
    fontSize: 12,
  },
  repostLink: {
    color: '#0A84FF',
    fontSize: 12,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  offersPill: {
    backgroundColor: '#0A84FF',
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 20,
  },
  offersPillText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
});
